import { motion } from "framer-motion";
import {
  PhoneIncoming, CalendarCheck, MessageSquare, Inbox, Bot, Star,
  ArrowRight, CheckCircle2, TrendingUp, Heart, Sparkles, Repeat, Mail,
  FileText, Send,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDemo } from "@/components/landing/DemoContext";

/* ============================================================================
   Lifecycle card hero visuals — product UI mockups (not stock photos)
   =========================================================================== */

/* Act 1 — Scheduling + Text back + AI ring */
const FillVisual = () => (
  <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-emerald-500/20 via-emerald-500/8 to-card">
    {/* dotted grid */}
    <div
      className="pointer-events-none absolute inset-0 opacity-50"
      style={{
        backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
        backgroundSize: "14px 14px",
        color: "rgba(16,185,129,0.18)",
      }}
    />

    {/* mini calendar tile - back layer */}
    <div className="absolute -right-4 top-3 w-36 -rotate-[8deg] rounded-lg border border-border bg-card/90 p-2 shadow-lg backdrop-blur">
      <div className="flex items-center justify-between">
        <span className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground">Mar</span>
        <CalendarCheck className="h-2.5 w-2.5 text-emerald-500" />
      </div>
      <div className="mt-1 grid grid-cols-7 gap-[2px]">
        {Array.from({ length: 14 }).map((_, i) => {
          const isFri = i === 11;
          return (
            <span
              key={i}
              className={`flex h-3 items-center justify-center rounded text-[7px] font-bold ${
                isFri ? "bg-emerald-500 text-white shadow shadow-emerald-500/40" : "bg-secondary/70 text-foreground/60"
              }`}
            >
              {i + 1}
            </span>
          );
        })}
      </div>
    </div>

    {/* AI ring badge - top left */}
    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-card/95 px-2 py-1 text-[9px] font-bold shadow-md backdrop-blur">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
      </span>
      <span className="text-emerald-700 dark:text-emerald-300">3 rings · AI</span>
    </div>

    {/* Text-back SMS card - front layer */}
    <div className="absolute bottom-3 left-3 right-12 rounded-xl border border-border bg-card/95 p-2.5 shadow-xl backdrop-blur">
      <div className="flex items-center gap-1.5 text-[9px] font-bold">
        <MessageSquare className="h-2.5 w-2.5 text-emerald-500" />
        <span className="text-foreground/85">Missed call · Auto text-back</span>
        <span className="ml-auto font-mono text-muted-foreground">7:42 PM</span>
      </div>
      <div className="mt-1.5 inline-block max-w-[78%] rounded-xl rounded-tl-sm bg-secondary/70 px-2 py-1 text-[10px] leading-tight">
        Sorry we missed you! Want me to book a time?
      </div>
      <div className="mt-1 ml-auto block max-w-[60%] rounded-xl rounded-tr-sm bg-gradient-to-br from-emerald-500 to-teal-500 px-2 py-1 text-right text-[10px] font-semibold leading-tight text-white shadow-sm">
        Yes — Fri 3pm
      </div>
      <div className="mt-1.5 flex items-center gap-1 rounded-md bg-emerald-500/10 px-1.5 py-1">
        <CheckCircle2 className="h-2.5 w-2.5 flex-none text-emerald-600" />
        <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-300">Booked · synced to EMR</span>
      </div>
    </div>
  </div>
);

/* Act 2 — Inbox + AI response */
const EngageVisual = () => (
  <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-violet-500/20 via-blue-500/8 to-card">
    <div
      className="pointer-events-none absolute inset-0 opacity-50"
      style={{
        backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
        backgroundSize: "14px 14px",
        color: "rgba(167,139,250,0.18)",
      }}
    />

    {/* Inbox card - top left */}
    <div className="absolute left-3 top-3 w-[58%] rounded-xl border border-border bg-card/95 p-2 shadow-xl backdrop-blur">
      <div className="flex items-center gap-1.5 border-b border-border pb-1.5 text-[9px] font-bold">
        <Inbox className="h-2.5 w-2.5 text-violet-500" />
        <span>Inbox</span>
        <span className="ml-auto rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[8px] text-violet-600 dark:text-violet-400">12 new</span>
      </div>
      <div className="mt-1 space-y-1">
        {[
          { who: "Sarah M.",  ch: "SMS",   tone: "bg-blue-500/15 text-blue-600 dark:text-blue-300",     Icon: MessageSquare, msg: "Running 10 min late" },
          { who: "Mark T.",   ch: "Call",  tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300", Icon: PhoneIncoming, msg: "Asked about pricing" },
          { who: "Emma K.",   ch: "DM",    tone: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300", Icon: MessageSquare, msg: "Saw your story" },
        ].map((r) => (
          <div key={r.who} className="flex items-center gap-1.5">
            <span className="inline-flex h-4 w-4 flex-none items-center justify-center rounded bg-secondary/70 text-foreground/65">
              <r.Icon className="h-2 w-2" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-[9px]">
                <span className="truncate font-bold">{r.who}</span>
                <span className={`flex-none rounded px-1 py-px text-[7.5px] font-bold uppercase tracking-wider ${r.tone}`}>{r.ch}</span>
              </div>
              <p className="truncate text-[8.5px] leading-tight text-muted-foreground">{r.msg}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* AI response card - bottom right, overlapping */}
    <div className="absolute bottom-3 right-3 w-[55%] rounded-xl border border-violet-500/40 bg-gradient-to-br from-violet-500/15 via-blue-500/8 to-card p-2 shadow-xl backdrop-blur">
      <div className="flex items-center gap-1 text-[9px] font-bold">
        <Sparkles className="h-2.5 w-2.5 text-violet-500" />
        <span className="text-violet-600 dark:text-violet-300">AI · Drafted reply</span>
        <span className="ml-auto font-mono text-[8px] text-muted-foreground">98%</span>
      </div>
      <div className="mt-1 rounded-md border border-border bg-background/80 px-1.5 py-1 text-[9px] leading-tight text-foreground/85">
        &ldquo;No problem — see you at 3:10!&rdquo;
      </div>
      <div className="mt-1 flex items-center gap-1">
        <button type="button" tabIndex={-1} aria-hidden className="inline-flex h-4 flex-1 items-center justify-center gap-0.5 rounded bg-gradient-to-r from-violet-600 to-blue-600 text-[8.5px] font-bold text-white shadow-sm">
          <Send className="h-2 w-2" /> Send
        </button>
        <span className="inline-flex h-4 items-center justify-center rounded border border-border bg-background px-1.5 text-[8.5px] font-bold">Edit</span>
      </div>
    </div>
  </div>
);

/* Act 3 — Reactivation list + Campaign */
const RetainVisual = () => (
  <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-amber-500/20 via-rose-500/8 to-card">
    <div
      className="pointer-events-none absolute inset-0 opacity-50"
      style={{
        backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
        backgroundSize: "14px 14px",
        color: "rgba(245,158,11,0.20)",
      }}
    />

    {/* Reactivation list - left */}
    <div className="absolute left-3 top-3 bottom-3 w-[55%] overflow-hidden rounded-xl border border-border bg-card/95 p-2 shadow-xl backdrop-blur">
      <div className="flex items-center gap-1.5 border-b border-border pb-1.5 text-[9px] font-bold">
        <Repeat className="h-2.5 w-2.5 text-amber-500" />
        <span>Reactivation</span>
        <span className="ml-auto rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[8px] text-amber-700 dark:text-amber-300">47 due</span>
      </div>
      <div className="mt-1 space-y-1">
        {[
          { who: "Sample 01", d: "182d", st: "Reactivate", tone: "bg-rose-500/15 text-rose-600 dark:text-rose-300" },
          { who: "Sample 02", d: "94d",  st: "Calling",    tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
          { who: "Sample 03", d: "127d", st: "Booked",     tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
          { who: "Sample 04", d: "76d",  st: "In series",  tone: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
        ].map((r) => (
          <div key={r.who} className="flex items-center gap-1.5 text-[9px]">
            <span className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-secondary/70 text-[6px] font-bold text-foreground/65">
              {r.who.slice(-2)}
            </span>
            <span className="min-w-0 flex-1 truncate font-bold">{r.who}</span>
            <span className="font-mono text-[8px] text-muted-foreground">{r.d}</span>
            <span className={`flex-none rounded px-1 py-px text-[7.5px] font-bold uppercase tracking-wider ${r.tone}`}>{r.st}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Campaign card - right */}
    <div className="absolute right-3 top-3 w-[35%] rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/15 to-card p-2 shadow-xl backdrop-blur">
      <div className="flex items-center gap-1 text-[9px] font-bold">
        <Mail className="h-2.5 w-2.5 text-amber-500" />
        <span className="text-amber-700 dark:text-amber-300">Campaign</span>
      </div>
      <p className="mt-1 font-display text-[10.5px] font-black leading-tight tracking-tight">Win-back · Q1</p>
      <div className="mt-1.5">
        <div className="flex items-center justify-between text-[8px] font-bold text-muted-foreground">
          <span>Sent</span>
          <span className="font-mono">247</span>
        </div>
        <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-secondary">
          <div className="h-full w-[78%] rounded-full bg-gradient-to-r from-amber-500 to-rose-500" />
        </div>
        <div className="mt-1 flex items-center justify-between text-[8px]">
          <span className="text-muted-foreground">Booked</span>
          <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">+31 · 12.5%</span>
        </div>
      </div>
    </div>

    {/* Review request floating - bottom right */}
    <div className="absolute bottom-3 right-3 w-[35%] rounded-xl border border-border bg-card/95 p-2 shadow-xl backdrop-blur">
      <div className="flex items-center gap-1 text-[9px] font-bold">
        <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" />
        <span>Review · 2h</span>
        <CheckCircle2 className="ml-auto h-2.5 w-2.5 text-emerald-500" />
      </div>
      <p className="mt-0.5 truncate text-[8.5px] leading-tight text-muted-foreground">Triggered post-visit</p>
    </div>
  </div>
);

const ACT_VISUAL = { fill: FillVisual, engage: EngageVisual, retain: RetainVisual };

/* ---------- Inline UI snippets (real-looking product overlays) ---------- */

const FillSnippet = () => (
  <div className="w-72 rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur-sm sm:w-80">
    <div className="flex items-center gap-2 text-xs font-medium">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <span className="text-muted-foreground">Incoming call · 7:42 PM</span>
    </div>
    <div className="mt-3 flex items-center gap-3 rounded-xl bg-secondary/70 p-3">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
        <PhoneIncoming className="h-4 w-4" />
      </span>
      <div className="text-xs">
        <div className="font-semibold">+1 (613) •••‑0142</div>
        <div className="text-muted-foreground">AI Receptionist · answering</div>
      </div>
    </div>
    <div className="mt-3 space-y-1.5">
      <div className="max-w-[85%] rounded-2xl bg-secondary/70 px-3 py-1.5 text-xs">
        I can book you in — Friday afternoon work?
      </div>
      <div className="ml-auto max-w-[85%] rounded-2xl bg-foreground px-3 py-1.5 text-xs text-background">
        Yes, 3pm please
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
      <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> Booked · Fri 3:00 PM
      </span>
      <span className="font-mono text-[10px] text-muted-foreground">→ Juvonno</span>
    </div>
  </div>
);

const EngageSnippet = () => (
  <div className="w-72 rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur-sm sm:w-80">
    <div className="flex items-center gap-2 border-b border-border pb-2.5">
      <Inbox className="h-3.5 w-3.5 text-violet-500" />
      <span className="font-display text-xs font-bold">Unified Inbox</span>
      <span className="ml-auto rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-500">
        12 new
      </span>
    </div>
    <div className="divide-y divide-border">
      {[
        { name: "Sarah M.", channel: "SMS", msg: "Running 10 min late", intent: "Update", color: "bg-blue-500/15 text-blue-600 dark:text-blue-400", Icon: MessageSquare },
        { name: "Mark T.", channel: "Call", msg: "Asked about pricing", intent: "New lead", color: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", Icon: PhoneIncoming },
        { name: "Emma K.", channel: "DM", msg: "Saw your story — am I a fit?", intent: "Booking", color: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400", Icon: MessageSquare },
      ].map((t) => (
        <div key={t.name} className="flex items-center gap-2.5 py-2">
          <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-secondary/70 text-foreground/70">
            <t.Icon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="truncate font-semibold">{t.name}</span>
              <span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                {t.channel}
              </span>
            </div>
            <p className="truncate text-[11px] text-muted-foreground">{t.msg}</p>
          </div>
          <span className={`flex-none rounded-full px-1.5 py-0.5 text-[9px] font-bold ${t.color}`}>{t.intent}</span>
        </div>
      ))}
    </div>
    <div className="mt-2 flex items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/10 px-2.5 py-2">
      <Bot className="h-3.5 w-3.5 flex-none text-violet-500" />
      <span className="text-[11px] leading-snug">
        <span className="font-semibold text-violet-500">Copilot:</span> Drafted 3 replies, ready to send
      </span>
    </div>
  </div>
);

const RetainSnippet = () => (
  <div className="w-72 rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur-sm sm:w-80">
    <div className="flex items-center justify-between border-b border-border pb-2.5">
      <div className="flex items-center gap-2">
        <Heart className="h-3.5 w-3.5 fill-rose-500 text-rose-500" />
        <span className="font-display text-xs font-bold">Patient lifecycle</span>
      </div>
      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
        +24 this week
      </span>
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2">
      {[
        { label: "Recall", v: "12" },
        { label: "Win-back", v: "8" },
        { label: "Reviews", v: "4" },
      ].map((s) => (
        <div key={s.label} className="rounded-lg bg-secondary/70 p-2 text-center">
          <div className="font-display text-base font-extrabold tracking-tight">{s.v}</div>
          <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center gap-2 rounded-lg bg-secondary/70 p-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
          <Star className="h-3 w-3 fill-current" />
        </span>
        <div className="min-w-0 flex-1 text-[11px]">
          <div className="font-semibold">Review request sent</div>
          <div className="truncate text-[10px] text-muted-foreground">Triggered 2h after visit · Sarah M.</div>
        </div>
        <CheckCircle2 className="h-3.5 w-3.5 flex-none text-emerald-500" />
      </div>
      <div className="flex items-center gap-2 rounded-lg bg-secondary/70 p-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-violet-500/15 text-violet-500">
          <CalendarCheck className="h-3 w-3" />
        </span>
        <div className="min-w-0 flex-1 text-[11px]">
          <div className="font-semibold">Recall reminder</div>
          <div className="truncate text-[10px] text-muted-foreground">6-month check-up · Mark T.</div>
        </div>
        <span className="text-[10px] font-bold text-violet-500">In 3d</span>
      </div>
    </div>
  </div>
);

/* ---------- Each act ---------- */

const ACTS = [
  {
    id: "fill",
    chapter: "Act 1",
    moment: "When a patient is trying to reach you",
    title: "Fill the schedule.",
    sub: "Every missed call, every form, every overflow moment caught in real time — without growing your front-desk team.",
    bullets: [
      "Missed call? Instant text back with a booking link",
      "3 rings, then AI picks up — books straight into your EMR",
      "Website form → instant task & lead with submission notes",
    ],
    replaces: ["Call answering service", "Booking widget", "Web form tool"],
    metric: { value: "3×", label: "answer rate" },
    outcome: "Booked → EMR",
    accent: "emerald",
  },
  {
    id: "engage",
    chapter: "Act 2",
    moment: "When the conversation is mid-flight",
    title: "Engage every patient.",
    sub: "Calls, texts, DMs and emails land in one inbox with intent and history attached — so every reply feels personal, and nothing slips.",
    bullets: [
      "Reply within 30 seconds, even at 9 PM",
      "30 minutes of silence? AI follows up before they ghost",
      "Pricing or insurance question? AI answers from your knowledge base",
    ],
    replaces: ["Inbox tool", "SMS app", "Social DMs", "Email tool"],
    metric: { value: "0", label: "missed follow-ups" },
    outcome: "Replied · synced",
    accent: "violet",
  },
  {
    id: "retain",
    chapter: "Act 3",
    moment: "After the visit",
    title: "Keep them coming back.",
    sub: "Turn one-time visits into a lifetime of care. Recall, reviews, win-backs and confirmations — quietly running in the background.",
    bullets: [
      "2 hours post-visit, review request fires automatically",
      "90 days dormant? Win-back sequence runs without front-desk lift",
      "Appointment check the day before — confirms, reschedules or cancels via SMS",
    ],
    replaces: ["Review tool", "Email marketing", "SMS reminders"],
    metric: { value: "32%", label: "fewer no-shows" },
    outcome: "Patient retained",
    accent: "amber",
  },
];

const ACCENT_MAP = {
  emerald: {
    chip: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
    dot: "bg-emerald-500",
    glow: "from-emerald-500/30 to-emerald-500/0",
    metricIcon: "text-emerald-600 dark:text-emerald-400",
  },
  violet: {
    chip: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/30",
    dot: "bg-violet-500",
    glow: "from-violet-500/30 to-violet-500/0",
    metricIcon: "text-violet-600 dark:text-violet-400",
  },
  amber: {
    chip: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    dot: "bg-amber-500",
    glow: "from-amber-500/30 to-amber-500/0",
    metricIcon: "text-amber-600 dark:text-amber-400",
  },
};

/* Single Act card — used in the 3-column lifecycle grid */
const Act = ({ act, index }) => {
  const tone = ACCENT_MAP[act.accent];
  const Visual = ACT_VISUAL[act.id];
  return (
    <motion.article
      data-testid={`lifecycle-act-${act.id}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: 0.08 * index }}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-all hover:-translate-y-1 hover:shadow-2xl"
    >
      {/* Hero visual — full product UI mockup on tablet+, simplified banner on mobile */}
      <div className="relative hidden aspect-[5/3] overflow-hidden sm:block">
        <div className={`absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-br ${tone.glow} blur-3xl`} />
        {Visual && <Visual />}
      </div>
      {/* Mobile: clean gradient banner with the act icon — reduces visual clutter */}
      <div className={`relative flex h-24 items-center justify-between overflow-hidden px-5 sm:hidden`}>
        <div className={`absolute inset-0 bg-gradient-to-br ${tone.glow} opacity-90`} />
        <div className="pointer-events-none absolute inset-0" style={{
          backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
          backgroundSize: "14px 14px",
          color: "rgba(255,255,255,0.10)",
        }} />
        <div className="relative flex items-center gap-2">
          <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-card/85 backdrop-blur ring-1 ring-inset ring-border ${tone.iconColor || ""}`}>
            <span className={`inline-flex h-5 w-5 items-center justify-center rounded ${tone.dot}`} />
          </span>
          <span className="rounded-full bg-card/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/80 backdrop-blur">
            {act.chapter}
          </span>
        </div>
        <span className="relative inline-flex items-center gap-1 rounded-full bg-foreground/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-background">
          <CheckCircle2 className="h-3 w-3" />
          {act.outcome}
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        {/* Act chip + outcome pill row — hidden on mobile (shown in banner above) */}
        <div className="mb-4 hidden items-center justify-between gap-3 sm:flex">
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] ${tone.chip}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
            {act.chapter}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/90 px-2.5 py-1 text-[10px] font-bold text-background">
            <CheckCircle2 className="h-3 w-3" /> {act.outcome}
          </span>
        </div>

        <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {act.moment}
        </p>
        <h3 className="mt-2 font-display text-2xl font-black leading-tight tracking-tight sm:text-[1.7rem]">
          {act.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {act.sub}
        </p>

        <ul className="mt-5 space-y-2.5">
          {act.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-[13px] leading-snug">
              <span className={`mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full ${tone.chip}`}>
                <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
              <span className="text-foreground/90">{b}</span>
            </li>
          ))}
        </ul>

        {/* Footer pinned to bottom */}
        <div className="mt-auto pt-6">
          <div className="border-t border-border pt-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Replaces
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {act.replaces.map((r) => (
                <span
                  key={r}
                  className="inline-flex items-center rounded-full bg-secondary/70 px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground line-through decoration-rose-400/60"
                >
                  {r}
                </span>
              ))}
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5">
              <TrendingUp className={`h-3.5 w-3.5 ${tone.metricIcon}`} />
              <span className="font-display text-lg font-black tracking-tight">{act.metric.value}</span>
              <span className="text-[11px] font-medium text-muted-foreground">{act.metric.label}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const ClinicLifecycle = () => {
  const openDemo = useDemo();
  return (
    <section
      id="lifecycle"
      data-testid="lifecycle-section"
      className="section-lifecycle relative bg-gradient-to-b from-slate-50/60 via-white to-slate-50/60 py-20 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        {/* Section intro */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
            How Clinic OS works
          </span>
          <h2 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            One OS for every moment of the{" "}
            <span className="text-gradient">patient journey.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            From the first missed call to the five-star review — Clinic OS quietly
            powers the three things every clinic owner needs: <strong className="text-foreground">fill the schedule, engage every patient, keep them coming back.</strong>
          </p>
        </div>

        {/* The three acts — 3-column grid */}
        <div className="mt-14 grid gap-6 sm:mt-16 sm:gap-7 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {ACTS.map((a, i) => (
            <Act key={a.id} act={a} index={i} />
          ))}
        </div>

        {/* Closing band */}
        <div className="mt-20 rounded-3xl border border-border bg-secondary/40 p-8 sm:p-12">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <h3 className="font-display text-2xl font-black tracking-tight sm:text-3xl">
                One system. One login. One team that finally has time to care.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                Clinic OS isn&apos;t another tool to learn. It&apos;s the one place your clinic runs from —
                replacing the messy stack you&apos;ve been duct-taping for years.
              </p>
            </div>
            <div className="flex flex-shrink-0 flex-col items-stretch gap-2 sm:items-end">
              <button
                data-testid="lifecycle-book-demo"
                onClick={openDemo}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-foreground px-6 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:opacity-90"
              >
                <CalendarCheck className="h-4 w-4" /> See it in 20 minutes
              </button>
              <Link
                to="/platform"
                data-testid="lifecycle-explore"
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                Or explore the platform <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
